import { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { Button } from "./ui/button";
import { useCart } from "../lib/cart";
import { toast } from "sonner";

export default function ItemOptionsDialog({ item, extras, open, onOpenChange }) {
  const { add } = useCart();
  const sides = item.sides || [];
  const allowed = extras.filter((e) => (item.extra_ids || []).includes(e.id));
  const [side, setSide] = useState(sides[0] || null);
  const [picked, setPicked] = useState([]);
  const [qty, setQty] = useState(1);

  useEffect(() => { if (open) { setSide(sides[0] || null); setPicked([]); setQty(1); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const chosen = allowed.filter((e) => picked.includes(e.id));
  const unit = item.price + chosen.reduce((a, e) => a + e.price, 0);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const confirm = () => {
    add(item, { side, extras: chosen.map(({ id, name, price }) => ({ id, name, price })), quantity: qty });
    toast.success(`${item.name} added`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="item-options-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{item.name}</DialogTitle>
          <DialogDescription>{item.description}</DialogDescription>
        </DialogHeader>

        {sides.length > 0 && (
          <div>
            <div className="text-sm font-semibold mb-2">Choose your side <span className="text-gray-400 font-normal">(free)</span></div>
            <div className="flex flex-wrap gap-2">
              {sides.map((s) => (
                <button key={s} type="button" onClick={() => setSide(s)} data-testid={`side-option-${s.toLowerCase()}`}
                  className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${side === s ? "bg-[#111111] text-white border-[#111111]" : "border-gray-200 hover:border-[#FF6600]"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {allowed.length > 0 && (
          <div>
            <div className="text-sm font-semibold mb-2">Add extras <span className="text-gray-400 font-normal">(optional)</span></div>
            <div className="space-y-1.5">
              {allowed.map((e) => (
                <label key={e.id} className="flex items-center gap-3 rounded-xl border border-gray-100 px-3 py-2 cursor-pointer hover:bg-[#FFF8F2]">
                  <Checkbox checked={picked.includes(e.id)} onCheckedChange={() => toggle(e.id)} data-testid={`extra-option-${e.id}`} />
                  <span className="flex-1 text-sm">{e.name}</span>
                  <span className="text-sm font-semibold text-[#CC1A00]">+ KSh {e.price}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 pt-2">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-9 w-9 grid place-items-center rounded-full bg-[#FFF1EE] text-[#CC1A00]" data-testid="options-qty-dec"><Minus size={14} /></button>
            <span className="w-6 text-center font-semibold" data-testid="options-qty">{qty}</span>
            <button type="button" onClick={() => setQty((q) => Math.min(50, q + 1))} className="h-9 w-9 grid place-items-center rounded-full mlobox-gradient text-white" data-testid="options-qty-inc"><Plus size={14} /></button>
          </div>
          <Button onClick={confirm} className="flex-1 rounded-full mlobox-gradient text-white font-semibold" data-testid="options-add-btn">
            Add to cart · KSh {unit * qty}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
